import { Box, Checkbox, FormControlLabel, Grid, MenuItem, Select, TextField } from "@mui/material";
import * as React from "react";
import { TreeCheckboxDropdown } from "ui/Scripts/source/components/framework/Dropdown/TreeCheckboxDropdown";
import { Base } from "ui/Scripts/source/framework/base";
import { ApiSuccess } from "ui/Scripts/source/models/common/apiSuccess";
import { EditItem } from "ui/Scripts/source/models/common/editItem";
import { IFileAttachment } from "ui/Scripts/source/models/common/fileAttachment";
import { IIdTitleNode, IdTitleNode } from "ui/Scripts/source/models/common/idTitleNode";
import { ConfirmationDialogResult, EnumHelper, RecordEducationScopeUnit, RecordUserAttachmentCategory } from "ui/Scripts/source/models/enums";
import { IRecordFurtherEducationGroupNode, RecordFurtherEducationGroupNode } from "ui/Scripts/source/models/recordFurtherEducationGroup/recordFurtherEducationGroupNode";
import { Translations } from "ui/Scripts/source/models/translations";
import { AppUtils } from "../../../framework/appUtils";
import { SaveDataObj } from "../../../framework/saveData";
import { IRecordEditItemFurtherEducation, RecordEditItemFurtherEducation, RecordEditItemFurtherEducationSave } from "../../../models/record/recordEditItemFurtherEducation";
import { IRecordEditItemQualification } from "../../../models/record/recordEditItemQualification";
import * as recordService from "../../../models/services/recordService";
import { StoreUtils } from "../../../models/store/storeUtils";
import { DatePicker } from "../../framework/DateTime/DatePicker";
import { Dialog } from "../../framework/Dialog/Dialog";
import { DialogButtons } from "../../framework/Dialog/DialogButtons";
import { DialogItemsNav } from "../../framework/Dialog/DialogItemsNav";
import { FormField } from "../../../../../../QTeamSystems.QTeamIntra.Ui/Scripts/source/components/framework/FormField";
import { GridRow } from "../../framework/Grid/GridRow";
import { RecordEditAttachmentList } from "../RecordEdit/RecordEditAttachmentList";
import { IRecordFurtherEducationNode } from "ui/Scripts/source/models/recordFurtherEducation/recordFurtherEducationNode";

export interface IRecordUserFurtherEducationDialogProp {
    classes?: string;
    companyId: string;
    recordId: string;
    userCode: string;
    furtherEducationGroups: IRecordFurtherEducationGroupNode[];
    recordQualifications: IRecordEditItemQualification[];
    item: IRecordEditItemFurtherEducation;
    attachments: IFileAttachment[];
    hasPrevious: boolean;
    hasNext: boolean;
    onCustomSave?: (saveItem: RecordEditItemFurtherEducationSave) => Promise<ApiSuccess>;
    onSaved: (item: IRecordEditItemFurtherEducation, closeDialog: boolean) => void;
    onClose: () => void;
    onPrevious: () => void;
    onNext: () => void;
    onAttachmentsRemoved: (ids: string[]) => void;
    onAttachmentsAdded: (attachments: IFileAttachment[]) => void;
}

export interface IRecordUserFurtherEducationDialogState {
    furtherEducations: IIdTitleNode[];
    furtherEducationId: string;
    scope: string;
    scopeReadOnly: boolean;
    scopeUnit: RecordEducationScopeUnit;
    scopeUnits: RecordEducationScopeUnit[];

    startDate: string;
    endDate: string;
    validityEndDate: string;
    validity: number;
    comment: string;
    activeState: number;
    attachments: IFileAttachment[];
}

export class RecordUserFurtherEducationDialog extends React.Component<IRecordUserFurtherEducationDialogProp, IRecordUserFurtherEducationDialogState> {
    private orgStateHash: string = "";
    
    getValidScopeUnits = (furtherEducation: IRecordFurtherEducationNode): RecordEducationScopeUnit[] => {
        let result: RecordEducationScopeUnit[] = [
            RecordEducationScopeUnit.Hour,
            RecordEducationScopeUnit.Day,
            RecordEducationScopeUnit.CourseCredit,
            RecordEducationScopeUnit.StudyWeek,
            RecordEducationScopeUnit.Course
        ];
        if (furtherEducation) {
            if (furtherEducation.scopeUnit !== 99) {
                result = [furtherEducation.scopeUnit as RecordEducationScopeUnit];
            }
        }
        return result;
    };
    
    private static setFurtherEducationTitle = (furtherEducations: IIdTitleNode[], qualifications: IRecordEditItemQualification[]) => {
        for (const furtherEducation of furtherEducations) {
            if (!furtherEducation.id) continue;
            const isQualification = qualifications.findIndex(i => i.furtherEducationId === furtherEducation.id) > -1;
            if (isQualification) {
                furtherEducation.title = furtherEducation.title + " (" + Translations.Qualification + ")";
            }
            RecordUserFurtherEducationDialog.setFurtherEducationTitle(furtherEducation.children, qualifications);
        }
    };

    private static getFurtherEducations = (furtherEducationGroups: IRecordFurtherEducationGroupNode[], qualifications: IRecordEditItemQualification[]): IIdTitleNode[] => {
        if (!furtherEducationGroups || furtherEducationGroups.length < 1) return [];
        const furtherEducations = RecordFurtherEducationGroupNode.getFurtherEducations(furtherEducationGroups);
        furtherEducations.unshift(IdTitleNode.createIdTitleNode("", `'${Translations.Select}'`, []));
        if (!qualifications || qualifications.length < 1) return furtherEducations;
        const result = furtherEducations.map(i => new IdTitleNode(i));
        RecordUserFurtherEducationDialog.setFurtherEducationTitle(result, qualifications);
        return result;
    };

    getStateFromProps = (props: IRecordUserFurtherEducationDialogProp): IRecordUserFurtherEducationDialogState => {
        const item = props.item;
        const furtherEducation = RecordFurtherEducationGroupNode.getRecordFurtherEducationById(this.props.furtherEducationGroups, item.furtherEducationId);
        const scopeUnits = this.getValidScopeUnits(furtherEducation);
        if (scopeUnits.length < 2) {
            if (scopeUnits[0] !== item.scopeUnit) {
                scopeUnits.push(item.scopeUnit);
            }
        }
        const startDate = Base.utcTimeToDateStr(item.startDate);
        const scope = furtherEducation?.scope ?? 0;
        const scopeReadOnly = scope > 0;
        const validity = furtherEducation?.validity ?? 0;
        const isNew = item.isNew();
        return {
            furtherEducations: RecordUserFurtherEducationDialog.getFurtherEducations(props.furtherEducationGroups, props.recordQualifications),
            furtherEducationId: item.furtherEducationId,
            scope: (isNew && scopeReadOnly
                ? scope
                : item.scope).toString(10),
            scopeReadOnly,
            scopeUnit: item.scopeUnit,
            scopeUnits,
            startDate,
            endDate: Base.utcTimeToDateStr(item.endDate),
            validityEndDate: Base.utcTimeToDateStr(isNew && item.startDate && validity > 0
                ? startDate.toUtcDate().addYears(validity).getTime()
                : item.validityEndDate),
            validity,
            comment: item.comment,
            activeState: item.activeState,
            attachments: props.attachments.filter(i => i.subOwnerId === item.id)
        };
    };

    constructor(props: IRecordUserFurtherEducationDialogProp) {
        super(props);
        this.state = this.getStateFromProps(props);
        this.orgStateHash = this.getHashFromState(props, this.state);
    }

    async componentDidUpdate(prevProps: IRecordUserFurtherEducationDialogProp, prevState: IRecordUserFurtherEducationDialogState) {
        const props = this.props;
        if (prevProps.item.getHash() === props.item.getHash() && EditItem.getHash(prevProps.attachments) === EditItem.getHash(props.attachments)) return;
        const state = this.getStateFromProps(props);
        this.setState(state);
        this.orgStateHash = this.getHashFromState(props, state);
    }

    handleChange = (event) => {
        const target = event.target;
        const value: string = target.value;
        const name: string = target.name;
        if (name === "scope") {
            this.setState({ scope: value });
        } else if (name === "scopeUnit") {
            this.setState({ scopeUnit: value.toInteger() });
        } else if (name === "comment") {
            this.setState({ comment: value });
        }
    };

    handleChangeFurtherEducationId = (furtherEducationIds: string[]) => {
        if (furtherEducationIds.length < 1) return;
        const furtherEducationId = furtherEducationIds[0];
        const furtherEducation = RecordFurtherEducationGroupNode.getRecordFurtherEducationById(this.props.furtherEducationGroups, furtherEducationId);
        if (!furtherEducation) return;
        const state = this.state;
        const scopeUnits = this.getValidScopeUnits(furtherEducation);
        const scopeReadOnly = furtherEducation.scope > 0;
        const validity = furtherEducation.validity;
        this.setState({
            furtherEducationId,
            scopeUnits,
            scopeUnit: scopeUnits.length < 2
                ? scopeUnits[0]
                : state.scopeUnit,
            scope: scopeReadOnly
                ? furtherEducation.scope.toString(10)
                : state.scope,
            scopeReadOnly,
            validityEndDate: validity > 0 && state.startDate
                ? Base.utcTimeToDateStr(state.startDate.toUtcDate().addYears(furtherEducation.validity).getTime())
                : "",
            validity
        });
    };

    handleBlur = (event) => {
        const target = event.target;
        const value: string = target.value;
        const name: string = target.name;
        if (name === "scope") {
            this.setState({ scope: value.toInteger().toString(10) });
        }
    };

    dateChange = (startDateStr: string, endDateStr: string) => {
        let newEndDateStr = endDateStr;
        if (startDateStr && endDateStr) {
            const startDate = startDateStr.toUtcDate();
            const endDate = endDateStr.toUtcDate();
            if (endDate < startDate) {
                newEndDateStr = startDateStr;
            };
        }
        const state = this.state;
        this.setState({
            startDate: startDateStr,
            endDate: newEndDateStr,
            validityEndDate: state.validity > 0 && startDateStr
                ? Base.utcTimeToDateStr(startDateStr.toUtcDate().addYears(state.validity).getTime())
                : state.validityEndDate
        });
    };

    handleStartDateChange = (value: string) => {
        this.dateChange(value, this.state.endDate);
    };

    handleEndDateChange = (value: string) => {
        this.dateChange(this.state.startDate, value);
    };

    handleValidityEndDateChange = (value: string) => {
        this.setState({ validityEndDate: value });
    };

    handleValidityEndDateBlur = (value: string) => {
        this.setState({
            validityEndDate: value ? Base.utcTimeToDateStr(value.toUtcDate().getTime()) : ""
        });
    };

    getHashFromState = (props: IRecordUserFurtherEducationDialogProp, state: IRecordUserFurtherEducationDialogState): string => {
        const result = new SaveDataObj();
        result.append("furtherEducationId", state.furtherEducationId);
        result.append("scope", state.scope);
        result.append("scopeUnit", state.scopeUnit);
        result.append("startDate", state.startDate);
        result.append("endDate", !state.endDate ? null : state.endDate);
        result.append("validityEndDate", !state.validityEndDate ? null : state.validityEndDate);
        result.append("comment", state.comment);
        result.append("activeState", state.activeState);
        return result.hash;
    };
    
    checkErrors = (): Promise<boolean> => {
        const state = this.state;
        return new Promise<boolean>((resolve) => {
            if (!state.furtherEducationId) {
                AppUtils.showErrorMessage(Translations.FurtherEducationCompetencyMustBeSelected);
                return resolve(false);
            }
            if (state.endDate) {
                const endDate = state.endDate.toUtcDate().getTime();
                const startDate = state.startDate.toUtcDate().getTime();
                if (endDate < startDate) {
                    AppUtils.showErrorMessage(Translations.TrainingEndDateCannotPrecedeTrainingStartDate);
                    return resolve(false);
                }
            }
            return resolve(true);
        });
    };

    getSaveData = (props: IRecordUserFurtherEducationDialogProp, recordFurtherEducation: RecordEditItemFurtherEducationSave): SaveDataObj => {
        const result = new SaveDataObj();
        result.append("id", props.recordId);
        result.append("companyId", props.companyId);
        result.append("userCode", props.userCode);
        result.append("furtherEducation", recordFurtherEducation);
        return result;
    };

    saveToDb = async (): Promise<RecordEditItemFurtherEducation> => {
        const props = this.props;
        const state = this.state;
        if (!await AppUtils.validate(this.checkErrors)) return null;
        // Set data
        const item = new RecordEditItemFurtherEducation(this.props.item);
        item.furtherEducationId = state.furtherEducationId;
        item.scope = state.scope.toInteger();
        item.scopeUnit = state.scopeUnit;
        item.startDate = state.startDate.toUtcDate().getTime();
        item.endDate = state.endDate ? state.endDate.toUtcDate().getTime() : null;
        item.validityEndDate = state.validityEndDate ? state.validityEndDate.toUtcDate().getTime() : null;
        item.comment = state.comment;
        item.activeState = state.activeState;
        if (props.onCustomSave) {
            const customResult = await props.onCustomSave(new RecordEditItemFurtherEducationSave(item));
            if (!customResult) return null;
            return item;
        }
        const saveData = this.getSaveData(this.props, new RecordEditItemFurtherEducationSave(item));
        // Call server
        const result = await AppUtils.callService(() => recordService.recordEditSaveFurtherEducation(saveData.obj));
        if (!result) return null;
        item.id = result.id;
        item.rowId = result.rowId;
        item.modifyUser = StoreUtils.getNowModifyUser();
        return item;
    };

    saveDialog = async (close: boolean) => {
        const result = await this.saveToDb();
        if (result) {
            this.props.onSaved(result, close);
        }
        return result;
    };

    handleSave = async (closeDialog: boolean): Promise<RecordEditItemFurtherEducation> => {
        return await this.saveDialog(closeDialog);
    };

    handleCancel = async () => {
        const props = this.props;
        const state = this.state;
        const cancelResult = await AppUtils.cancel(this.getHashFromState(props, state), this.orgStateHash, null);
        if (cancelResult === ConfirmationDialogResult.Cancel) return;
        if (cancelResult === ConfirmationDialogResult.Yes) {
            this.handleSave(true);
        } else {
            props.onClose();
        }
    };

    canMoveOut = async (): Promise<boolean> => {
        const props = this.props;
        return await AppUtils.canMoveOut(this.getHashFromState(props, this.state), this.orgStateHash,
            async () => !!await this.saveDialog(false),
            null,
            (newStateHash) => { this.orgStateHash = newStateHash; }
        );
    };

    handlePrevious = async () => {
        if (!await this.canMoveOut()) return;
        this.props.onPrevious();
    };

    handleNext = async () => {
        if (!await this.canMoveOut()) return;
        this.props.onNext();
    };

    render() {
        const props = this.props;
        const state = this.state;
        const isNew = props.item.isNew();
        return (
            <Dialog
                id="record-user-further-educatin-dialog"
                maxWidth="sm"
                title={isNew ? Translations.AddFurtherEducation + " / " + Translations.Competency : Translations.EditFurtherEducation + " / " + Translations.Competency + ": " + props.item.furtherEducationCode}
                body={
                    <>
                        <GridRow>
                            <Grid item xs>
                                <FormField title={Translations.Education + " / " + Translations.Competency} margin={0}>
                                    <TreeCheckboxDropdown
                                        required={true}
                                        selectedIds={[state.furtherEducationId]}
                                        singleSelect={true}
                                        values={state.furtherEducations}
                                        onChange={this.handleChangeFurtherEducationId}
                                    />
                                </FormField>
                            </Grid>
                        </GridRow>
                        <GridRow sx={{ alignItems: "end" }}>
                            <Grid item xs>
                                <FormField title={Translations.Scope} required>
                                    <TextField type="text" name="scope" title={Translations.Scope} inputProps={{ maxLength: 8 }} value={state.scope} disabled={state.scopeReadOnly} required={true} onChange={this.handleChange} onBlur={this.handleBlur} />
                                </FormField>
                            </Grid>
                            <Grid item xs>
                                <FormField title={Translations.ScopeUnit}>
                                    <Select name="scopeUnit" title={Translations.ScopeUnit} value={state.scopeUnit.toString(10)} onChange={this.handleChange} disabled={state.scopeUnits.length < 2}>
                                        {state.scopeUnits.map(scopeUnit =>
                                            <MenuItem key={scopeUnit} value={scopeUnit.toString(10)}>{EnumHelper.RecordEducationScopeUnitToStr(scopeUnit)}</MenuItem>
                                        )}
                                    </Select>
                                </FormField>
                            </Grid>
                            <Grid item xs>
                                <FormField title="">
                                    <FormControlLabel
                                        label={Translations.Archived}
                                        control={
                                            <Checkbox
                                                checked={state.activeState < 0.5}
                                                onChange={(_e, checked: boolean) => this.setState({ activeState: checked ? 0 : 1 })}
                                            />
                                        }
                                    />
                                </FormField>
                            </Grid>
                        </GridRow>
                        <GridRow>
                            <Grid item xs>
                                <FormField title={Translations.TrainingStartDate} required>
                                    <DatePicker
                                        value={state.startDate}
                                        required={true}
                                        onChange={this.handleStartDateChange}
                                    />
                                </FormField>
                            </Grid>
                            <Grid item xs>
                                <FormField title={Translations.TrainingEndDate}>
                                    <DatePicker
                                        value={state.endDate}
                                        onChange={this.handleEndDateChange}
                                    />
                                </FormField>
                            </Grid>
                            <Grid item xs>
                                <FormField title={Translations.ValidityEndDate}>
                                    <DatePicker
                                        value={state.validityEndDate}
                                        onChange={this.handleValidityEndDateChange}
                                    />
                                </FormField>
                            </Grid>
                        </GridRow>
                        <GridRow>
                            <Grid item xs>
                                <FormField title={Translations.AdditionalInformation}>
                                    <TextField name="comment" title={Translations.AdditionalInformation} inputProps={{ maxLength: 1000 }} multiline minRows={5} disabled={false} value={state.comment} onChange={this.handleChange} />
                                </FormField>
                            </Grid>
                        </GridRow>
                        {!!props.onAttachmentsAdded &&
                            <GridRow>
                                <Grid item xs>
                                    <RecordEditAttachmentList
                                        isDisabled={isNew}
                                        isDisabledMessage={Translations.NewFurtherEducationMustBeSavedBeforeAttachmentsCanBeAdded}
                                        companyId={props.companyId}
                                        recordId={props.recordId}
                                        subOwnerId={props.item.id}
                                        userCode={props.userCode}
                                        category={RecordUserAttachmentCategory.FurtherEducation}
                                        attachments={state.attachments}
                                        onRemoved={props.onAttachmentsRemoved}
                                        onAdded={props.onAttachmentsAdded}
                                    />
                                </Grid>
                            </GridRow>
                        }
                    </>
                }
                actions={
                    <Box display="flex" justifyContent="space-between" width="100%">
                        <DialogItemsNav
                            modifyUser={!isNew ? props.item.modifyUser : null}
                            hasPrevious={props.hasPrevious}
                            hasNext={props.hasNext}
                            onPrevious={!isNew ? this.handlePrevious : null}
                            onNext={!isNew ? this.handleNext : null}
                        />
                        <DialogButtons
                            isNew={isNew}
                            onCancel={this.handleCancel}
                            onSave={() => this.handleSave(false)}
                        />
                    </Box>
                }
                onClose={this.handleCancel}/>
        );
    }
}